import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Container,
  CenterHeader,
  HeaderTitle,
  HeaderSubtitle,
  GrayInfoBox,
  FieldLabel,
  FieldValue,
  ActionButtonContainer,
  RejectButton,
  VerifyButton
} from './styles';
import { Grid } from '@mui/material';
import dayjs from 'dayjs';
import {
  organizationActionOnShiftAttendance,
  selectUpdateShiftAttendanceLoading,
  selectUpdateShiftAttendanceSuccess,
  selectUpdateShiftAttendanceError,
  selectUpdateShiftAttendanceMessage,
  resetUpdateShiftAttendance,
} from '../../../../modules/shift';
import AlertPopup from '../../../../components/common/AlertPopup';
import ActionPopup from '../../../../components/common/ActionPopup';

const InfoItem = ({ label, value }: { label: string, value: string }) => (
  <>
    <FieldLabel sx={{ mb: 1 }}>{label}</FieldLabel>
    <FieldValue>{value}</FieldValue>
  </>
);

interface VerifyShiftProps {
  clockIn: string | Date | null;
  clockOut?: string | Date | null;
  workerRemarks?: string;
  workerSuggestedTime?: number | null;
  assignmentId: number;
  shiftId: number;
  shiftStart?: string;
  shiftEnd?: string;
  onSuccess: (action: 'accept' | 'reject', message?: string) => void;
}

const VerifyShift: React.FC<VerifyShiftProps> = ({
  clockIn,
  clockOut,
  workerRemarks,
  workerSuggestedTime,
  assignmentId,
  shiftId,
  shiftStart,
  shiftEnd,
  onSuccess
}) => {
  const dispatch = useDispatch<any>();
  const loading = useSelector(selectUpdateShiftAttendanceLoading);
  const success = useSelector(selectUpdateShiftAttendanceSuccess);
  const error = useSelector(selectUpdateShiftAttendanceError);
  const message = useSelector(selectUpdateShiftAttendanceMessage);

  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');
  const [openActionPopup, setOpenActionPopup] = useState(false);
  const [actionType, setActionType] = useState<'accept' | 'reject' | null>(null);

  useEffect(() => {
    dispatch(resetUpdateShiftAttendance());
  }, [dispatch, shiftId]);

  const handleOpenActionPopup = (action: 'accept' | 'reject') => {
    setActionType(action);
    setOpenActionPopup(true);
  };

  const handleActionSubmit = (remark?: string) => {
    if (actionType) {
      dispatch(organizationActionOnShiftAttendance({
        shift_id: shiftId,
        shift_assignment_id: assignmentId,
        action: actionType,
        remarks: remark || '',
      }));
    }
    setOpenActionPopup(false);
  };

  useEffect(() => {
    if (success) {
      if (actionType) {
        onSuccess(actionType, message || 'Shift attendance updated successfully');
      }
    }
  }, [success, message, onSuccess, actionType]);

  useEffect(() => {
    if (error) {
      setAlertMessage(error || 'Failed to update shift attendance');
      setAlertSeverity('error');
      setOpenAlert(true);
    }
  }, [error]);

  const shiftDuration = (shiftStart && shiftEnd)
    ? `${dayjs(shiftEnd).diff(dayjs(shiftStart), 'hour', true).toFixed(1)} hours`
    : '-';

  return (
    <Container>
      <CenterHeader>
        <HeaderTitle>Verify Shift Times & Remarks</HeaderTitle>
        <HeaderSubtitle>Review and give feedback for this worker</HeaderSubtitle>
      </CenterHeader>

      <GrayInfoBox>
        <Grid container spacing={3}>
          <Grid size={6}>
            <InfoItem label="Shift Start" value={shiftStart ? dayjs(shiftStart).format('HH:mm') : "-"} />
          </Grid>
          <Grid size={6}>
            <InfoItem label="Shift End" value={shiftEnd ? dayjs(shiftEnd).format('HH:mm') : "-"} />
          </Grid>
          <Grid size={12}>
            <InfoItem label="Shift Duration" value={shiftDuration} />
          </Grid>

          <Grid size={6}>
            <InfoItem label="Clock In" value={clockIn ? dayjs(clockIn).format('HH:mm') : "-"} />
          </Grid>
          <Grid size={6}>
            <InfoItem label="Clock Out" value={clockOut ? dayjs(clockOut).format('HH:mm') : "-"} />
          </Grid>

          {workerSuggestedTime !== undefined && workerSuggestedTime !== null && (
            <Grid size={12}>
              <InfoItem label="Worker Suggested Time" value={`${workerSuggestedTime} hours`} />
            </Grid>
          )}

          <Grid size={12}>
            <InfoItem
              label="Remarks"
              value={workerRemarks ? workerRemarks : "No remarks provided."}
            />
          </Grid>
        </Grid>
      </GrayInfoBox>

      <ActionButtonContainer>
        <RejectButton variant="outlined" onClick={() => handleOpenActionPopup('reject')} disabled={loading || success}>
          Reject
        </RejectButton>
        <VerifyButton variant="contained" onClick={() => handleOpenActionPopup('accept')} disabled={loading || success}>
          {loading && actionType === 'accept' ? 'Verifying...' : 'Verify'}
        </VerifyButton>
      </ActionButtonContainer>
      <AlertPopup
        open={openAlert}
        message={alertMessage}
        severity={alertSeverity}
        onClose={() => setOpenAlert(false)}
      />
      <ActionPopup
        open={openActionPopup}
        onClose={() => setOpenActionPopup(false)}
        onSubmit={handleActionSubmit}
        title={actionType === 'accept' ? 'Verify Shift' : 'Reject Shift'}
        message={actionType === 'accept' ? 'Please enter any remarks for this shift.' : 'Please provide a reason for rejecting this shift.'}
        submitButtonText={actionType === 'accept' ? 'Verify' : 'Reject'}
        loading={loading}
      />
    </Container>
  );
};

export default VerifyShift;
